import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import amplifyService from '../services/amplify.service.ts';
import { FaChartLine, FaPuzzlePiece, FaClock } from 'react-icons/fa';
import { Line } from 'react-chartjs-2';
import { Chessboard } from 'react-chessboard';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler
);

type ProfileProps = {
  user: any;
};

function Profile({ user }: ProfileProps) {
  const { username } = useParams<{ username?: string }>();
  const [stats, setStats] = useState<{ score: number; count: number; time: number; name?: string | null; createdAt?: string } | null>(null);
  const [solvedPuzzles, setSolvedPuzzles] = useState<Array<{
    id: string;
    whitePlayer: string;
    blackPlayer: string;
    date: string;
    moveIndex: number;
    fen: string;
    score?: number;
    time?: number;
    solvedAt?: number;
  }>>([]);
  const [loadingPuzzles, setLoadingPuzzles] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [copiedFenId, setCopiedFenId] = useState<string | null>(null);
  const [chartTimeRange, setChartTimeRange] = useState<'1M' | '3M' | '6M' | 'ALL'>('1M');
  const [puzzleScoresForChart, setPuzzleScoresForChart] = useState<{ [key: string]: number }>({});
  const [puzzleTimesForChart, setPuzzleTimesForChart] = useState<{ [key: string]: number }>({});
  const [puzzleSolvedAtForChart, setPuzzleSolvedAtForChart] = useState<{ [key: string]: number }>({});
  const [hoveredPuzzle, setHoveredPuzzle] = useState<{
    id: string;
    fen: string;
    top: number;
    left: number;
    orientation: 'white' | 'black';
  } | null>(null);
  const hoveredPuzzleFenRef = useRef<string | null>(null);
  const hoverTimeoutRef = useRef<number | null>(null);

  // Sorting state
  const [sortBy, setSortBy] = useState<'whitePlayer' | 'blackPlayer' | 'moveIndex' | 'score' | 'time' | 'date' | 'solvedAt' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [favoritePuzzles, setFavoritePuzzles] = useState<string[]>([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const handleSort = (column: 'whitePlayer' | 'blackPlayer' | 'moveIndex' | 'score' | 'time' | 'date' | 'solvedAt') => {
    const defaultOrder = (column === 'date' || column === 'score' || column === 'solvedAt') ? 'desc' : 'asc';

    if (sortBy === column) {
      // Aynı sütuna tıklandı
      if (sortOrder === defaultOrder) {
        // Şu anki sıralama varsayılan ise, tersine çevir
        setSortOrder(defaultOrder === 'asc' ? 'desc' : 'asc');
      } else {
        // Şu anki sıralama tersine çevrilmiş ise, sıralamayı kaldır (null)
        setSortBy(null);
        setSortOrder('desc'); // Sıralama kaldırıldığında, bir sonraki tıklama için varsayılan 'desc' olarak ayarla
      }
    } else {
      // Farklı sütuna tıklandı -> O sütunu seç ve varsayılan sıraya koy
      setSortBy(column);
      setSortOrder(defaultOrder);
    }
  };

  const renderSortIcon = (column: 'whitePlayer' | 'blackPlayer' | 'moveIndex' | 'score' | 'time' | 'date' | 'solvedAt') => {
    const isDescDefault = column === 'date' || column === 'score' || column === 'solvedAt';
    const isActive = sortBy === column;
    const arrow = isActive
      ? (sortOrder === 'asc' ? '↑' : '↓')
      : (isDescDefault ? '↓' : '↑');

    return (
      <span style={{
        fontSize: '1.1rem', // Ok boyutu büyütüldü
        marginLeft: '2px',
        opacity: isActive ? 1 : 0.25, // Aktif değilse soluk
        transition: 'all 0.2s',
        display: 'inline-block',
        lineHeight: 0.8,
        verticalAlign: 'middle',
        color: isActive ? '#60a5fa' : '#a3bffa'
      }}>
        {arrow}
      </span>
    );
  };

  const sortedPuzzles = useMemo(() => {
    let filtered = solvedPuzzles;

    if (showFavoritesOnly) {
      filtered = solvedPuzzles.filter(p => favoritePuzzles.includes(p.id));
    }

    if (!sortBy) return filtered; // Sıralama yoksa filtrelenmiş listeyi döndür

    const sorted = [...filtered].sort((a, b) => {
      let aVal: any = '';
      let bVal: any = '';

      switch (sortBy) {
        case 'whitePlayer':
          aVal = (a.whitePlayer || '').toLowerCase();
          bVal = (b.whitePlayer || '').toLowerCase();
          break;
        case 'blackPlayer':
          aVal = (a.blackPlayer || '').toLowerCase();
          bVal = (b.blackPlayer || '').toLowerCase();
          break;
        case 'moveIndex':
          aVal = a.moveIndex || 0;
          bVal = b.moveIndex || 0;
          break;
        case 'score':
          aVal = a.score || 0;
          bVal = b.score || 0;
          break;
        case 'time':
          aVal = a.time || 0;
          bVal = b.time || 0;
          break;
        case 'date':
          // Date string format: "YYYY-MM-DD" or similar
          aVal = new Date(a.date).getTime();
          bVal = new Date(b.date).getTime();
          break;
        case 'solvedAt':
          aVal = a.solvedAt || 0;
          bVal = b.solvedAt || 0;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [solvedPuzzles, sortBy, sortOrder, showFavoritesOnly, favoritePuzzles]);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const load = async () => {
      // Eğer URL'de username varsa, o kullanıcının profilini göster; yoksa kendi profilini göster
      const targetUsername = username || (user ? user.username : null);
      if (!targetUsername) return;

      try {
        const dbUser: any = await amplifyService.getUser(targetUsername);
        if (dbUser) {
          // sync local cache for chart meta (sadece kendi profilimiz için)
          if (targetUsername === (user ? user.username : null)) {
            const cacheKey = `UserData_${targetUsername}`;
            const existing = localStorage.getItem(cacheKey);
            const prev = existing ? JSON.parse(existing) : {};
            localStorage.setItem(cacheKey, JSON.stringify({
              ...prev,
              id: dbUser.id,
              name: dbUser.name,
              score: dbUser.score || 0,
              count: dbUser.count || 0,
              time: dbUser.time || 0,
              createdAt: dbUser.createdAt,
              updatedAt: dbUser.updatedAt,
            }));
          }

          // Set favorite puzzles
          if ((dbUser as any).favoritePuzzles && Array.isArray((dbUser as any).favoritePuzzles)) {
            setFavoritePuzzles((dbUser as any).favoritePuzzles.filter((p: any) => p !== null));
          } else {
            setFavoritePuzzles([]);
          }

          // Load solved puzzles
          if (dbUser.solvedPuzzles && Array.isArray(dbUser.solvedPuzzles) && dbUser.solvedPuzzles.length > 0) {
            setLoadingPuzzles(true);
            try {
              const puzzles = await amplifyService.getPuzzlesByIds(dbUser.solvedPuzzles);
              // Puzzle puanlarını veritabanından al, string ise parse et; yoksa localStorage'dan al (geriye dönük uyumluluk için)
              let puzzleScores: { [key: string]: number } = {};
              try {
                if (dbUser.puzzleScores) {
                  if (typeof dbUser.puzzleScores === 'string') {
                    const parsed = JSON.parse(dbUser.puzzleScores);
                    if (parsed && typeof parsed === 'object') puzzleScores = parsed as { [key: string]: number };
                  } else if (typeof dbUser.puzzleScores === 'object') {
                    puzzleScores = dbUser.puzzleScores as { [key: string]: number };
                  }
                }
                if (!puzzleScores || Object.keys(puzzleScores).length === 0) {
                  const cacheKey = `UserData_${targetUsername}`;
                  const localData = localStorage.getItem(cacheKey);
                  puzzleScores = localData ? (JSON.parse(localData).puzzleScores || {}) : {};
                }
              } catch (_err) {
                puzzleScores = {};
              }

              // Sadece puanı olan puzzle'ları sayarak average score ve count hesapla (Leaderboard mantığı)
              const scores = Object.values(puzzleScores).filter(
                (score): score is number => score !== undefined && score !== null && !Number.isNaN(score) && typeof score === 'number'
              );
              const scoredPuzzleCount = scores.length;
              const calculatedScore = scoredPuzzleCount > 0
                ? scores.reduce((acc, score) => acc + score, 0) / scoredPuzzleCount
                : 0;

              // puzzleTimes'ı al ve sadece puanı olan puzzle'ların time'ını topla
              let puzzleTimes: { [key: string]: number } = {};
              try {
                if ((dbUser as any).puzzleTimes) {
                  if (typeof (dbUser as any).puzzleTimes === 'string') {
                    const parsed = JSON.parse((dbUser as any).puzzleTimes);
                    if (parsed && typeof parsed === 'object') puzzleTimes = parsed as { [key: string]: number };
                  } else if (typeof (dbUser as any).puzzleTimes === 'object') {
                    puzzleTimes = (dbUser as any).puzzleTimes as { [key: string]: number };
                  }
                }
              } catch (_err) {
                puzzleTimes = {};
              }

              // Sadece puanı olan puzzle'ların time'ını topla (puzzleScores ile aynı puzzle ID'leri)
              const totalTime = Object.keys(puzzleScores)
                .filter(puzzleId => puzzleScores[puzzleId] !== undefined && puzzleScores[puzzleId] !== null && !Number.isNaN(puzzleScores[puzzleId]))
                .reduce((acc, puzzleId) => acc + (puzzleTimes[puzzleId] || 0), 0);

              // Stats'ı güncelle - sadece puanı olan puzzle'ları say
              setStats({
                score: calculatedScore,
                count: scoredPuzzleCount,
                time: totalTime,
                name: (dbUser as any).name || null,
                createdAt: dbUser.createdAt,
              });


              // puzzleSolvedAt'ı al (puzzle çözülme zamanları)
              let puzzleSolvedAt: { [key: string]: number } = {};
              try {
                if ((dbUser as any).puzzleSolvedAt) {
                  if (typeof (dbUser as any).puzzleSolvedAt === 'string') {
                    const parsed = JSON.parse((dbUser as any).puzzleSolvedAt);
                    if (parsed && typeof parsed === 'object') puzzleSolvedAt = parsed as { [key: string]: number };
                  } else if (typeof (dbUser as any).puzzleSolvedAt === 'object') {
                    puzzleSolvedAt = (dbUser as any).puzzleSolvedAt as { [key: string]: number };
                  }
                }
              } catch (_err) {
                puzzleSolvedAt = {};
              }

              // Chart için puzzleScores, puzzleTimes ve puzzleSolvedAt'ı state'e set et
              setPuzzleScoresForChart(puzzleScores);
              setPuzzleTimesForChart(puzzleTimes);
              setPuzzleSolvedAtForChart(puzzleSolvedAt);

              // Score olmayan puzzle'ları tespit et
              const puzzlesWithoutScore = puzzles.filter(p =>
                puzzleScores[p.id] === undefined ||
                puzzleScores[p.id] === null
              );

              if (puzzlesWithoutScore.length > 0) {
                console.warn(`⚠️ ${puzzlesWithoutScore.length} solved puzzles have no score recorded.`);
                console.warn('   These puzzles were likely solved before the scoring system was implemented.');
                console.log('   Missing scores for puzzle IDs:', puzzlesWithoutScore.map(p => p.id).slice(0, 10));
              }

              setSolvedPuzzles(puzzles.map(p => ({
                id: p.id,
                whitePlayer: p.whitePlayer,
                blackPlayer: p.blackPlayer,
                date: p.date,
                moveIndex: p.moveIndex,
                fen: p.fen || '',
                score: puzzleScores[p.id] !== undefined ? puzzleScores[p.id] : undefined,
                time: puzzleTimes[p.id] !== undefined ? puzzleTimes[p.id] : undefined,
                solvedAt: puzzleSolvedAt[p.id] !== undefined ? puzzleSolvedAt[p.id] : undefined,
              })).reverse());
            } catch (error) {
              console.error('Error loading solved puzzles:', error);
            } finally {
              setLoadingPuzzles(false);
            }
          } else {
            // Eğer solvedPuzzles yoksa, puzzleScores'dan direkt hesapla
            let puzzleScores: { [key: string]: number } = {};
            try {
              if (dbUser.puzzleScores) {
                if (typeof dbUser.puzzleScores === 'string') {
                  const parsed = JSON.parse(dbUser.puzzleScores);
                  if (parsed && typeof parsed === 'object') puzzleScores = parsed as { [key: string]: number };
                } else if (typeof dbUser.puzzleScores === 'object') {
                  puzzleScores = dbUser.puzzleScores as { [key: string]: number };
                }
              }
            } catch (_err) {
              puzzleScores = {};
            }

            // Sadece puanı olan puzzle'ları sayarak average score ve count hesapla
            const scores = Object.values(puzzleScores).filter(
              (score): score is number => score !== undefined && score !== null && !Number.isNaN(score) && typeof score === 'number'
            );
            const scoredPuzzleCount = scores.length;
            const calculatedScore = scoredPuzzleCount > 0
              ? scores.reduce((acc, score) => acc + score, 0) / scoredPuzzleCount
              : 0;

            // puzzleTimes'ı al ve sadece puanı olan puzzle'ların time'ını topla
            let puzzleTimes: { [key: string]: number } = {};
            try {
              if ((dbUser as any).puzzleTimes) {
                if (typeof (dbUser as any).puzzleTimes === 'string') {
                  const parsed = JSON.parse((dbUser as any).puzzleTimes);
                  if (parsed && typeof parsed === 'object') puzzleTimes = parsed as { [key: string]: number };
                } else if (typeof (dbUser as any).puzzleTimes === 'object') {
                  puzzleTimes = (dbUser as any).puzzleTimes as { [key: string]: number };
                }
              }
            } catch (_err) {
              puzzleTimes = {};
            }

            // Sadece puanı olan puzzle'ların time'ını topla (puzzleScores ile aynı puzzle ID'leri)
            const totalTime = Object.keys(puzzleScores)
              .filter(puzzleId => puzzleScores[puzzleId] !== undefined && puzzleScores[puzzleId] !== null && !Number.isNaN(puzzleScores[puzzleId]))
              .reduce((acc, puzzleId) => acc + (puzzleTimes[puzzleId] || 0), 0);

            // Stats'ı güncelle
            setStats({
              score: calculatedScore,
              count: scoredPuzzleCount,
              time: totalTime,
              name: (dbUser as any).name || null,
              createdAt: dbUser.createdAt,
            });


            // puzzleSolvedAt'ı al (puzzle çözülme zamanları)
            let puzzleSolvedAt: { [key: string]: number } = {};
            try {
              if ((dbUser as any).puzzleSolvedAt) {
                if (typeof (dbUser as any).puzzleSolvedAt === 'string') {
                  const parsed = JSON.parse((dbUser as any).puzzleSolvedAt);
                  if (parsed && typeof parsed === 'object') puzzleSolvedAt = parsed as { [key: string]: number };
                } else if (typeof (dbUser as any).puzzleSolvedAt === 'object') {
                  puzzleSolvedAt = (dbUser as any).puzzleSolvedAt as { [key: string]: number };
                }
              }
            } catch (_err) {
              puzzleSolvedAt = {};
            }

            // Chart için puzzleScores, puzzleTimes ve puzzleSolvedAt'ı state'e set et
            setPuzzleScoresForChart(puzzleScores);
            setPuzzleTimesForChart(puzzleTimes);
            setPuzzleSolvedAtForChart(puzzleSolvedAt);
          }

          return; // prefer DB
        }
      } catch (_e) {
        // ignore and fallback to local
      }

      // Fallback to local cache if DB unreachable (sadece kendi profilimiz için)
      if (targetUsername === (user ? user.username : null)) {
        const key = `UserData_${targetUsername}`;
        const local = localStorage.getItem(key) || localStorage.getItem('UserData');
        if (local) {
          const parsed = JSON.parse(local);
          setStats({
            score: parsed.score || 0,
            count: parsed.count || 0,
            time: parsed.time || 0,
            createdAt: parsed.createdAt,
          });
        }
      }
    };
    load();
  }, [user, username]);

  const averageTime = stats && stats.count && stats.count > 0 ? (stats.time || 0) / stats.count : 0;
  const minutes = Math.floor(averageTime / 60);
  const seconds = Math.floor(averageTime % 60);

  // Average score'u sadece puanı olan puzzle'lardan hesapla
  const calculatedAverageScore = useMemo(() => {
    const puzzlesWithScores = solvedPuzzles.filter(p => p.score !== undefined && p.score !== null && !Number.isNaN(p.score));
    if (puzzlesWithScores.length === 0) return 0;
    const sum = puzzlesWithScores.reduce((acc, p) => acc + (p.score || 0), 0);
    return sum / puzzlesWithScores.length;
  }, [solvedPuzzles]);
  const targetUsername = username || (user ? user.username : null);

  const initials = useMemo(() => {
    try {
      // Eğer başka bir kullanıcının profilini görüntülüyorsak, stats'tan name al
      if (username && stats) {
        const display = (stats.name || username || 'U').toString();
        return display.trim().charAt(0).toUpperCase();
      }
      // Kendi profilimiz için
      const key = user ? `UserData_${user.username}` : 'UserData';
      const local = localStorage.getItem(key);
      const cached = local ? JSON.parse(local) : null;
      const display = (
        cached?.name ||
        user?.attributes?.name ||
        (user?.attributes?.email ? (user.attributes.email as string).split('@')[0] : '') ||
        user?.username ||
        ''
      ).toString();
      if (!display) return 'U';
      return display.trim().charAt(0).toUpperCase();
    } catch (_e) {
      return 'U';
    }
  }, [user, username, stats]);

  const displayName = useMemo(() => {
    try {
      // Eğer başka bir kullanıcının profilini görüntülüyorsak, stats'tan name al
      if (username && stats) {
        return stats.name || username || 'User';
      }
      // Kendi profilimiz için
      const key = user ? `UserData_${user.username}` : 'UserData';
      const local = localStorage.getItem(key);
      const cached = local ? JSON.parse(local) : null;
      const display = (
        cached?.name ||
        user?.attributes?.name ||
        (user?.attributes?.email ? (user.attributes.email as string).split('@')[0] : '') ||
        user?.username ||
        ''
      ).toString();
      return display || 'User';
    } catch (_e) {
      return 'User';
    }
  }, [user, username, stats]);

  const formatPuzzleScore = useCallback((value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return '-';
    }
    return (value * 100).toLocaleString('tr-TR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }, []);

  const getScorePalette = useCallback((value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return {
        text: '#e5e7eb',
        background: 'rgba(148, 163, 184, 0.18)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
      };
    }
    const diff = Math.abs(value * 100);
    if (diff < 3) {
      return {
        text: '#68d391',
        background: 'rgba(104, 211, 145, 0.18)',
        border: '1px solid rgba(104, 211, 145, 0.28)',
      };
    }
    if (diff < 10) {
      return {
        text: '#f6ad55',
        background: 'rgba(246, 173, 85, 0.18)',
        border: '1px solid rgba(246, 173, 85, 0.28)',
      };
    }
    return {
      text: '#f56565',
      background: 'rgba(245, 101, 101, 0.18)',
      border: '1px solid rgba(245, 101, 101, 0.28)',
    };
  }, []);

  const boardPreviewSize = isMobile ? 200 : 220;

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current !== null) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const scheduleHoverHide = () => {
    clearHoverTimeout();
    hoverTimeoutRef.current = window.setTimeout(() => {
      setHoveredPuzzle(null);
      hoverTimeoutRef.current = null;
    }, 120);
  };

  const handleFenHoverStart = (
    event: React.MouseEvent<HTMLSpanElement>,
    puzzle: { id: string; fen: string }
  ) => {
    if (!puzzle.fen) {
      setHoveredPuzzle(null);
      return;
    }
    clearHoverTimeout();
    const rect = event.currentTarget.getBoundingClientRect();
    let top = rect.top;
    let left = rect.right + 16;
    if (left + boardPreviewSize > window.innerWidth - 12) {
      left = rect.left - boardPreviewSize - 16;
      if (left < 12) {
        left = Math.max(12, window.innerWidth - boardPreviewSize - 12);
      }
    }
    if (top + boardPreviewSize > window.innerHeight - 12) {
      top = Math.max(12, window.innerHeight - boardPreviewSize - 12);
    }
    top = Math.max(12, top);

    const fenParts = puzzle.fen.split(' ');
    const orientation =
      fenParts.length > 1 && fenParts[1] === 'b' ? 'black' : 'white';

    setHoveredPuzzle({
      id: puzzle.id,
      fen: puzzle.fen,
      top,
      left,
      orientation,
    });
    hoveredPuzzleFenRef.current = puzzle.fen;
  };

  const handleFenHoverEnd = () => {
    scheduleHoverHide();
    hoveredPuzzleFenRef.current = null;
  };

  useEffect(() => {
    return () => {
      clearHoverTimeout();
    };
  }, []);

  const chartData = useMemo(() => {
    // 1. Prepare events (time and score) using puzzleSolvedAt
    const events: Array<{ t: number; score: number }> = [];

    Object.keys(puzzleSolvedAtForChart).forEach(pid => {
      const timestamp = puzzleSolvedAtForChart[pid]; // Unix timestamp in seconds
      const s = puzzleScoresForChart[pid];
      if (timestamp && s !== undefined && s !== null && !Number.isNaN(s)) {
        events.push({ t: timestamp * 1000, score: s }); // Convert to milliseconds
      }
    });
    // Sort by time ascending
    events.sort((a, b) => a.t - b.t);

    // Determine start date based on time range filter
    let startDate: Date;
    const now = new Date();

    if (chartTimeRange === '1M') {
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
    } else if (chartTimeRange === '3M') {
      startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    } else if (chartTimeRange === '6M') {
      startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    } else {
      // 'ALL'
      let createdAtStr = stats?.createdAt;
      // Fallback to user prop if available (only if viewing own profile)
      if (!createdAtStr && user?.createdAt && (!username || username === user.username)) {
        createdAtStr = user.createdAt;
      }
      // Default to createdAt or 30 days ago
      startDate = createdAtStr ? new Date(createdAtStr) : new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());

      // Safety check: ensure start date is reasonable (e.g. not 1970)
      if (startDate.getFullYear() < 2020) {
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      }
    }

    startDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const labels: string[] = [];
    const data: number[] = [];

    // 3. Calculate Cumulative Average Day by Day
    let runningSum = 0;
    let runningCount = 0;
    let eventIdx = 0;

    // Pre-process events before start date (if any)
    while (eventIdx < events.length && events[eventIdx].t < startDate.getTime()) {
      runningSum += events[eventIdx].score;
      runningCount++;
      eventIdx++;
    }

    // If no events at all, but we have stats (legacy/fallback)
    if (events.length === 0 && (stats?.score ?? 0) > 0) {
      const fallbackScore = stats!.score;
      for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
        const dateLabel = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        labels.push(dateLabel);
        data.push(Number((fallbackScore * 100).toFixed(1)));
      }
    } else {
      // Normal loop: calculate cumulative average up to end of each day
      for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
        const endOfDay = new Date(d);
        endOfDay.setHours(23, 59, 59, 999);

        while (eventIdx < events.length && events[eventIdx].t <= endOfDay.getTime()) {
          runningSum += events[eventIdx].score;
          runningCount++;
          eventIdx++;
        }

        const avg = runningCount > 0 ? runningSum / runningCount : 0;
        const dateLabel = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        labels.push(dateLabel);
        data.push(Number((avg * 100).toFixed(1)));
      }
    }

    return {
      labels,
      datasets: [
        {
          label: 'Average Score',
          data,
          borderColor: '#60a5fa',
          backgroundColor: 'rgba(96,165,250,0.18)',
          fill: true,
          tension: 0.3,
          pointRadius: 3,
          pointHoverRadius: 7,
          pointHitRadius: 12,
          pointBackgroundColor: '#60a5fa',
          pointBorderWidth: 0,
        }
      ]
    };
  }, [user, username, stats, puzzleScoresForChart, puzzleSolvedAtForChart, chartTimeRange]);
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        callbacks: {
          label: (ctx: any) => `Score: ${Number(ctx.parsed.y).toFixed(1)}`,
        }
      }
    },
    interaction: { mode: 'nearest', intersect: false },
    scales: {
      x: { ticks: { color: '#a3bffa' }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { min: 0, suggestedMin: 0, ticks: { color: '#a3bffa' }, grid: { color: 'rgba(255,255,255,0.05)' } }
    }
  } as const;


  return (
    <div style={{ width: '100%', maxWidth: '1200px', paddingBottom: '2rem' }}>
      <div className="profile-hero" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '10px', marginBottom: '1.1rem' }}>
        <div className="avatar" style={{ marginBottom: '4px' }}>{initials}</div>
        <h2 style={{ margin: 0 }}>{displayName}</h2>
        <p style={{ marginTop: '0.2rem', opacity: 0.85 }}>User stats and progress</p>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1rem'
      }}>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ color: '#60a5fa' }}><FaChartLine /></div>
            <div className="stat-label">Average Score</div>
          </div>
          <div className="stat-value">
            {(calculatedAverageScore * 100).toFixed(1)}
          </div>
        </div>

        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ color: '#34d399' }}><FaPuzzlePiece /></div>
            <div className="stat-label">Solved Puzzles</div>
          </div>
          <div className="stat-value">
            {stats ? stats.count ?? 0 : 0}
          </div>
        </div>

        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ color: '#fbbf24' }}><FaClock /></div>
            <div className="stat-label">Average Time</div>
          </div>
          <div className="stat-value">
            {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
          </div>
        </div>
      </div>

      <div style={{ marginTop: '1.25rem' }}>
        <div className="stat-card" style={{ padding: '1rem', display: 'flex', flexDirection: 'column' }}>
          <div style={{
            display: 'flex',
            marginBottom: '1rem',
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            borderRadius: '6px',
            border: '1px solid rgba(148,163,184,0.1)',
            width: 'fit-content'
          }}>
            {['1M', '3M', '6M', 'ALL'].map((range) => (
              <button
                key={range}
                onClick={() => setChartTimeRange(range as '1M' | '3M' | '6M' | 'ALL')}
                style={{
                  background: chartTimeRange === range ? 'rgba(96, 165, 250, 0.15)' : 'transparent',
                  color: chartTimeRange === range ? '#93c5fd' : '#94a3b8',
                  border: 'none',
                  borderRight: range !== 'ALL' ? '1px solid rgba(148,163,184,0.1)' : 'none',
                  padding: '6px 14px',
                  fontSize: '0.85rem',
                  fontWeight: chartTimeRange === range ? 600 : 400,
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  borderTopLeftRadius: range === '1M' ? '5px' : '0',
                  borderBottomLeftRadius: range === '1M' ? '5px' : '0',
                  borderTopRightRadius: range === 'ALL' ? '5px' : '0',
                  borderBottomRightRadius: range === 'ALL' ? '5px' : '0',
                }}
              >
                {range}
              </button>
            ))}
          </div>

          <div style={{ height: '260px', width: '100%' }}>
            <Line data={chartData} options={{ ...chartOptions, maintainAspectRatio: false }} />
          </div>
        </div>
      </div>

      {solvedPuzzles.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <div className="stat-card" style={{ padding: '1rem' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              margin: '0 0 1rem 0',
              paddingBottom: '0.75rem',
              borderBottom: '2px solid rgba(255,255,255,0.15)'
            }}>
              <h3 style={{ margin: 0, color: '#e5e7eb', fontSize: '1.1rem', fontWeight: 600, textAlign: 'left' }}>
                Solved Puzzles
              </h3>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                color: showFavoritesOnly ? '#fc8181' : '#a3bffa',
                fontSize: '0.9rem',
                fontWeight: 500,
                transition: 'color 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={showFavoritesOnly}
                  onChange={(e) => setShowFavoritesOnly(e.target.checked)}
                  style={{ cursor: 'pointer', accentColor: '#fc8181' }}
                />
                Favorites
              </label>
            </div>
            {loadingPuzzles ? (
              <div style={{ color: '#a3bffa', textAlign: 'center', padding: '2rem', opacity: 0.8 }}>
                Loading puzzles...
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', color: '#e5e7eb' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      <th
                        onClick={() => handleSort('whitePlayer')}
                        style={{
                          textAlign: 'left',
                          padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                          fontWeight: 600,
                          color: '#a3bffa',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          White Player
                          {renderSortIcon('whitePlayer')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('blackPlayer')}
                        style={{
                          textAlign: 'left',
                          padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                          fontWeight: 600,
                          color: '#a3bffa',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          Black Player
                          {renderSortIcon('blackPlayer')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('moveIndex')}
                        style={{
                          textAlign: 'left',
                          padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                          fontWeight: 600,
                          color: '#a3bffa',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          cursor: 'pointer',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          Move Index
                          {renderSortIcon('moveIndex')}
                        </div>
                      </th>
                      <th style={{ textAlign: 'left', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', fontWeight: 600, color: '#a3bffa', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                        FEN
                      </th>
                      <th
                        onClick={() => handleSort('score')}
                        style={{
                          textAlign: 'right',
                          padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                          fontWeight: 600,
                          color: '#a3bffa',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' }}>
                          Score
                          {renderSortIcon('score')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('time')}
                        style={{
                          textAlign: 'right',
                          padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                          fontWeight: 600,
                          color: '#a3bffa',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' }}>
                          Time
                          {renderSortIcon('time')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('date')}
                        style={{
                          textAlign: 'right',
                          padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                          fontWeight: 600,
                          color: '#a3bffa',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          cursor: 'pointer',
                          userSelect: 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' }}>
                          Date
                          {renderSortIcon('date')}
                        </div>
                      </th>
                      <th
                        onClick={() => handleSort('solvedAt')}
                        style={{
                          textAlign: 'right',
                          padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem',
                          fontWeight: 600,
                          color: '#a3bffa',
                          fontSize: isMobile ? '0.8rem' : '0.9rem',
                          cursor: 'pointer',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '2px' }}>
                          Solved At
                          {renderSortIcon('solvedAt')}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPuzzles.map((puzzle, index) => {
                      const palette = getScorePalette(puzzle.score);
                      return (
                        <tr
                          key={puzzle.id}
                          style={{
                            backgroundColor: index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)';
                          }}
                        >
                          <td style={{ textAlign: 'left', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', fontSize: isMobile ? '0.85rem' : '0.9rem' }}>
                            {puzzle.whitePlayer}
                          </td>
                          <td style={{ textAlign: 'left', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', fontSize: isMobile ? '0.85rem' : '0.9rem' }}>
                            {puzzle.blackPlayer}
                          </td>
                          <td style={{ textAlign: 'left', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', fontSize: isMobile ? '0.85rem' : '0.9rem', opacity: 0.8 }}>
                            {puzzle.moveIndex}
                          </td>
                          <td style={{ textAlign: 'left', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', fontSize: isMobile ? '0.85rem' : '0.9rem', opacity: 0.8 }}>
                            <span
                              onClick={() => {
                                if (puzzle.fen) {
                                  navigator.clipboard.writeText(puzzle.fen).then(() => {
                                    setCopiedFenId(puzzle.id);
                                    setTimeout(() => {
                                      setCopiedFenId(null);
                                    }, 2000);
                                  }).catch(err => {
                                    console.error('Failed to copy FEN:', err);
                                  });
                                }
                              }}
                              style={{
                                cursor: 'pointer',
                                color: copiedFenId === puzzle.id ? '#68d391' : '#60a5fa',
                                textDecoration: 'underline',
                                textDecorationColor: 'rgba(96, 165, 250, 0.5)',
                                transition: 'color 0.2s, font-weight 0.2s',
                                fontWeight: copiedFenId === puzzle.id ? '600' : 'normal'
                              }}
                              onMouseEnter={(e) => {
                                handleFenHoverStart(e, puzzle);
                                if (copiedFenId !== puzzle.id) {
                                  e.currentTarget.style.color = '#93c5fd';
                                }
                              }}
                              onMouseLeave={(e) => {
                                handleFenHoverEnd();
                                if (copiedFenId !== puzzle.id) {
                                  e.currentTarget.style.color = '#60a5fa';
                                }
                              }}
                              title="Click to copy full FEN"
                            >
                              {copiedFenId === puzzle.id
                                ? '✓ Copied!'
                                : (puzzle.fen ? (puzzle.fen.length > 10 ? `${puzzle.fen.substring(0, 10)}...` : puzzle.fen) : 'N/A')
                              }
                            </span>
                          </td>
                          <td style={{ textAlign: 'right', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', fontSize: isMobile ? '0.85rem' : '0.9rem', opacity: 0.9, fontWeight: 600, color: palette.text }}>
                            {formatPuzzleScore(puzzle.score)}
                          </td>
                          <td style={{ textAlign: 'right', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', fontSize: isMobile ? '0.85rem' : '0.9rem', opacity: 0.8 }}>
                            {puzzle.time !== undefined && puzzle.time !== null && !Number.isNaN(puzzle.time)
                              ? `${Math.floor(puzzle.time / 60)}:${(puzzle.time % 60).toString().padStart(2, '0')}`
                              : '-'
                            }
                          </td>
                          <td style={{ textAlign: 'right', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', fontSize: isMobile ? '0.85rem' : '0.9rem', opacity: 0.8 }}>
                            {puzzle.date && !Number.isNaN(new Date(puzzle.date.replace(/\./g, '-')).getTime())
                              ? new Date(puzzle.date.replace(/\./g, '-')).toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' })
                              : (puzzle.date || '-')
                            }
                          </td>
                          <td style={{ textAlign: 'right', padding: isMobile ? '0.5rem 0.75rem' : '0.75rem 1rem', fontSize: isMobile ? '0.85rem' : '0.9rem', opacity: 0.8 }}>
                            {puzzle.solvedAt
                              ? new Date(puzzle.solvedAt * 1000).toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' })
                              : '-'
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      {hoveredPuzzle && hoveredPuzzle.fen && (
        <div
          onClick={() => {
            const fenToUse = hoveredPuzzleFenRef.current || hoveredPuzzle.fen;
            if (fenToUse && fenToUse.trim()) {
              // Lichess format: /analysis/standard/{fen} where spaces are replaced with underscores
              const formattedFen = fenToUse.trim().replace(/\s+/g, '_');
              window.open(`https://lichess.org/analysis/standard/${formattedFen}`, '_blank');
            }
          }}
          onMouseEnter={(e) => {
            clearHoverTimeout();
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 20px 45px rgba(15, 23, 42, 0.55)';
          }}
          onMouseLeave={(e) => {
            handleFenHoverEnd();
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 18px 40px rgba(15, 23, 42, 0.45)';
          }}
          style={{
            position: 'fixed',
            top: hoveredPuzzle.top,
            left: hoveredPuzzle.left,
            zIndex: 1300,
            backgroundColor: '#0f172a',
            borderRadius: '12px',
            border: '1px solid rgba(148, 163, 184, 0.35)',
            boxShadow: '0 18px 40px rgba(15, 23, 42, 0.45)',
            padding: '0.5rem',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
        >
          <div style={{ cursor: 'pointer' }}>
            <Chessboard
              position={hoveredPuzzle.fen}
              boardWidth={boardPreviewSize}
              arePiecesDraggable={false}
              animationDuration={0}
              boardOrientation={hoveredPuzzle.orientation}
              customBoardStyle={{
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.6)',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;


